//src/components/HomePage.js
import CircularNavigation from './CircularNavigation.js';
import { Helmet } from 'react-helmet-async';
import ivmalogo from '../assets/ivmaofbc124x59_2x.png';
import avettalogo from '../assets/Avetta-Logo.png';
import isclogo from '../assets/ISClogo.png';
import worksafebclogo from '../assets/worksafebcstamp.jpg';
import isnetworldlogo from '../assets/isnetworld-logo.png';
import HeroSection from './HeroSection.js';
import carouselCrew from '../assets/carousel-crew.jpg';
import carouselTruck from '../assets/carousel-truck.jpg';
import heroVideo from '../assets/applicators_walking_into_distance.mp4';
import highrail from '../assets/highrail.jpg';
import highrailWebp from '../assets/highrail.webp';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import StatsCounter from './StatsCounter.js';

const HomePage = () => {
  return (
    <div className='bg-custom-earth'>
      <div className="wood-container">
        <div className="flex flex-col min-h-screen text-gray-900">
          <Helmet>
            <title>Industrial Vegetation Management BC | Boultbee</title>
            <meta name="description" content="BC's trusted industrial herbicide applicator. Weed control for railroads, utilities, and municipalities. Get a free quote today." />
            <link rel="canonical" href="https://www.weedmanager.com/" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Industrial Vegetation Management BC | Boultbee" />
            <meta property="og:description" content="BC's trusted industrial herbicide applicator. Weed control for railroads, utilities, and municipalities. Get a free quote today." />
            <meta property="og:url" content="https://www.weedmanager.com/" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-home.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Industrial Vegetation Management BC | Boultbee" />
            <meta name="twitter:description" content="BC's trusted industrial herbicide applicator. Weed control for railroads, utilities, and municipalities. Get a free quote today." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-home.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" }
                ]
              })}
            </script>
          </Helmet>


          {/* Hero Section — falls back to carousel when no video provided */}
          <HeroSection video={heroVideo} poster={carouselCrew} images={[highrail, carouselTruck, carouselCrew]} homepage />


          {/* 3 round main website sections */}
          <CircularNavigation />


          {/* How We Work — 4-step process summary */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center w-full mb-6">How We Work</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
                {/* Horizontal connector line (desktop only) */}
                <div className="hidden lg:block absolute top-5 left-[12.5%] right-[12.5%] h-px bg-gray-300" aria-hidden="true" />
                {[
                  { step: '1', title: 'Site Assessment', desc: 'We evaluate your site using satellite imagery, aerial photos, or an on-site visit to identify target species, assess conditions, and scope the work — so we arrive prepared with the right plan.' },
                  { step: '2', title: 'Custom Plan', desc: 'We design a herbicide program with IPM-compliant products, rates, and timing tailored to your site.' },
                  { step: '3', title: 'GPS-Tracked Application', desc: 'Flow-controlled spray systems with real-time GPS tracking ensure precise, documented coverage.' },
                  { step: '4', title: 'Invoicing & Follow-Up', desc: 'You receive detailed invoicing with line items and area descriptions. For ongoing programs, we monitor results and adjust treatment plans as needed.' },
                ].map((item) => (
                  <div key={item.step} className="text-center relative">
                    <div className="w-10 h-10 rounded-full bg-green-700 text-white font-bold text-lg flex items-center justify-center mx-auto mb-3 relative z-10">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6">
                <Link to="/processes" className="text-green-700 font-semibold hover:underline text-sm">
                  View Our Full Process &rarr;
                </Link>
              </div>
            </div>
          </AnimateOnScroll>


          {/* Value Proposition Section */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center w-full">Why BC's Largest Industrial Clients Trust Boultbee</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-1">
                    <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" /></svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">40+ Years of Proven Performance</h3>
                    <p className="text-gray-700 mt-2">Decades of vegetation management expertise, operating continuously in British Columbia's toughest industrial environments.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-1">
                    <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /><path strokeLinecap="round" d="M12 2v4M12 18v4M2 12h4M18 12h4" /></svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Technology-Driven Precision</h3>
                    <p className="text-gray-700 mt-2">GPS-tracked applications, flow-controlled spray systems, variable analysis, and comprehensive spray area measurements.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-1">
                    <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Full Regulatory Compliance</h3>
                    <p className="text-gray-700 mt-2">Avetta certified, ISNetworld member, WorkSafeBC compliant, IVMA member. Every technician holds a current BC Industrial Vegetation & Noxious Weed Pesticide Applicator Certificate.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-1">
                    <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Large BC Coverage Area</h3>
                    <p className="text-gray-700 mt-2">From the Lower Mainland to Prince George, we deploy crews across BC with the specialized equipment and local knowledge each region demands.</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 mt-8 pt-6 border-t border-gray-200">
                <img src={avettalogo} alt="Avetta Certified" className="h-10 w-auto hover-color" loading="lazy" width={400} height={399} />
                <img src={isnetworldlogo} alt="ISNetworld Member Vendor" className="h-10 w-auto hover-color" loading="lazy" width={660} height={660} />
                <img src={ivmalogo} alt="IVMA Member" className="h-10 w-auto hover-color" loading="lazy" width={248} height={117} />
                <img src={worksafebclogo} alt="WorkSafeBC Compliant" className="h-10 w-auto hover-color" loading="lazy" width={843} height={841} />
                <img src={isclogo} alt="ISC of BC" className="h-10 w-auto hover-color" loading="lazy" width={225} height={155} />
              </div>
            </div>
          </AnimateOnScroll>

          {/* Quote tagline strip */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="bg-green-900 py-8 text-center rounded-lg mx-auto w-[98%]">
              <p className="text-white text-xl md:text-2xl font-serif italic max-w-2xl mx-auto px-4">
                "The right herbicide, at the right rate, in the right place, at the right time."
              </p>
            </div>
          </AnimateOnScroll>

          {/* Stats Counter */}
          <StatsCounter />


          {/* Equipment & Technology Section */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1 overflow-hidden rounded-lg shadow-md">
                  <picture>
                    <source srcSet={highrailWebp} type="image/webp" />
                    <img
                      src={highrail}
                      alt="Boultbee high-rail equipped herbicide spray truck operating on railroad track"
                      className="w-full h-auto transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                      width={1280}
                      height={960}
                    />
                  </picture>
                </div>
                <div className="order-1 lg:order-2">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Purpose-Built Equipment for Every Environment</h2>
                  <p className="text-gray-700 mb-3">Our fleet includes high-rail equipped spray trucks for railroad corridors, rotomist turbine sprayers for hillside and difficult-terrain applications, and truck-mounted systems for industrial sites and municipal infrastructure.</p>
                  <p className="text-gray-700 mb-4">Every vehicle features GPS tracking and flow-controlled spray systems that ensure consistent application rates and precise coverage across the entire treatment area.</p>
                  <Link to="/processes" className="text-green-700 font-semibold hover:underline">
                    View Our Full Process &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </AnimateOnScroll>


          {/* Industries Served Section */}
          <AnimateOnScroll animation="fade-in-up" delay={100}>
            <div className="text-container-blur">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center w-full">Specialized Vegetation Management for Your Industry</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                <div>
                  <Link to="/industries/railroad" className="hover:text-green-700 transition-colors flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-700 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 18h8M8 18l-2 3m10-3 2 3M6 6h12v8a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V6Zm0 0V4h12v2M9 10h6" /></svg>
                    <h3 className="text-xl font-bold text-gray-900">Railroads</h3>
                  </Link>
                  <p className="text-gray-700 mt-2">High-rail herbicide application, ballast weed control, crossing visibility, signal line clearance, and right-of-way vegetation management.</p>
                </div>
                <div>
                  <Link to="/industries/oil-gas" className="hover:text-green-700 transition-colors flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-700 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2C12 2 7 8.5 7 13a5 5 0 0 0 10 0c0-4.5-5-11-5-11Z" /></svg>
                    <h3 className="text-xl font-bold text-gray-900">Oil & Gas / Refineries</h3>
                  </Link>
                  <p className="text-gray-700 mt-2">Bare ground programs for tank farms, pipeline corridors, pump stations, and refinery perimeters with full safety documentation.</p>
                </div>
                <div>
                  <Link to="/industries/municipal" className="hover:text-green-700 transition-colors flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-700 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V8.25l-9-6.75-9 6.75V21h4.5" /></svg>
                    <h3 className="text-xl font-bold text-gray-900">Municipalities</h3>
                  </Link>
                  <p className="text-gray-700 mt-2">Sidewalk and brickwork weed control, median maintenance, park turf management, sportsfield programs, and noxious weed compliance.</p>
                </div>
                <div>
                  <Link to="/industries/utility" className="hover:text-green-700 transition-colors flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-700 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" /></svg>
                    <h3 className="text-xl font-bold text-gray-900">Utilities</h3>
                  </Link>
                  <p className="text-gray-700 mt-2">Power line corridor vegetation management, substation bare ground control, and cable strip maintenance for power, gas, and telecommunications providers.</p>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-700 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
                    <h3 className="text-xl font-bold text-gray-900">Pulp & Paper</h3>
                  </div>
                  <p className="text-gray-700 mt-2">Mill yard vegetation control, log sort area management, and perimeter weed prevention programs.</p>
                </div>
                <div className="flex items-center justify-center">
                  <Link to="/contact" className="cta-button">
                    Request a Quote for Your Industry
                  </Link>
                </div>
              </div>
            </div>
          </AnimateOnScroll>


          {/* Service Areas Regional Callout */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center w-full mb-6">Serving BC</h2>
              <div className="flex flex-wrap justify-center gap-4">
                {[
                  { to: '/service-areas/okanagan', name: 'Okanagan & Similkameen', desc: 'Penticton, Kelowna, Vernon, and surrounding communities' },
                  { to: '/service-areas/kootenays', name: 'Kootenays & Columbia Valley', desc: 'Trail, Castlegar, Nelson, Invermere, and the Kootenays' },
                  { to: '/service-areas/interior-bc', name: 'Interior BC & Cariboo', desc: 'Kamloops, Merritt, Revelstoke, and the Thompson-Nicola' },
                  { to: '/service-areas/northern-bc', name: 'Prince George & North', desc: 'Prince George, Quesnel, Vanderhoof, and surrounding area' },
                  { to: '/service-areas/lower-mainland', name: 'Lower Mainland & Fraser Valley', desc: 'Surrey, Langley, Abbotsford, and the Fraser Valley' },
                ].map((region) => (
                  <Link
                    key={region.to}
                    to={region.to}
                    className="block bg-white/80 rounded-lg p-5 text-center hover-lift border border-gray-200 w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]"
                  >
                    <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                    <h3 className="text-base font-bold text-gray-900 mb-1">{region.name}</h3>
                    <p className="text-gray-700 text-sm">{region.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </AnimateOnScroll>


          {/* Bottom CTA */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="bg-green-900 py-12 px-4 text-center rounded-lg mx-auto w-[98%] mb-6">
              <h2 className="text-white text-2xl md:text-3xl font-bold mb-4">Ready to Solve Your Vegetation Challenge?</h2>
              <p className="text-white/80 text-lg mb-6 max-w-xl mx-auto">Get a free site assessment from BC's most experienced industrial herbicide applicator.</p>
              <Link to="/contact" className="cta-button" style={{ backgroundColor: 'white', color: '#005e30' }}>Request a Free Site Assessment</Link>
            </div>
          </AnimateOnScroll>

        </div>
      </div>
    </div>

  );

};

export default HomePage;
